import { THREETween } from "./THREETween";
import { GSAPTween } from "./GSAPTween";
import type { GSAPTweenLike } from "./gsapFactory";
import type { Timeline } from "./Timeline";

export interface Core {
    id?: string;

    duration: () => number;
    progress: (value?: number) => number;
    then: (
        onFulfilled?: ((value: Omit<this, "then">) => any) | null,
        onRejected?: ((reason: any) => any) | null
    ) => Promise<this>;
    revert: () => void;
    kill: () => void;
    play: () => void;
    resume: () => void;
    pause: () => void;
    reset: () => void; 
    repeat: (n: number) => void;
    playPause: (time?: number) => void;
}

export interface Tween extends Core {
    eventCallback: (type: "onStart" | "onUpdate" | "onComplete", cb: () => void) => void;
    delay: (v?: number) => void | number | null; 
}

export interface AnimationContext extends Omit<Core, 'then'> {
    then: (callback?: () => void) => Promise<void>;
    cleanup: () => void;
    add: (t: GsapTween | THREETween | Timeline) => void;
    delete: (t: GsapTween | THREETween | Timeline) => void;
    clear: () => void;
    get: (id: string) => GsapTween | THREETween | undefined;
}

export type GsapTween = GSAPTween | GSAPTweenLike;

export enum EaseOptions {
  None = "none",
  Power1In = "power1.in",
  Power1Out = "power1.out",
  Power1InOut = "power1.inOut",
  Power2In = "power2.in",
  Power2Out = "power2.out",
  Power2InOut = "power2.inOut",
  Power3In = "power3.in",
  Power3Out = "power3.out",
  Power3InOut = "power3.inOut",
  BackIn = "back.in",
  BackOut = "back.out",
  BackInOut = "back.inOut",
  ElasticIn = "elastic.in",
  ElasticOut = "elastic.out",
  ElasticInOut = "elastic.inOut",
  BounceIn = "bounce.in",
  BounceOut = "bounce.out",
  BounceInOut = "bounce.inOut",
}

export type SharedOpts = {
  duration?: number;
  delay?: number;
  repeat?: number;
  yoyo?: boolean;
};

export type AngleOpts = SharedOpts & {
  ease?: EaseOptions;
  minAzimuth: number;
  maxAzimuth: number;
  minPolar: number;
  maxPolar: number;
};

export type PositionOpts = SharedOpts & {
  ease?: EaseOptions;
  position: number;
  keyframes?: number[];
};

export type BlurOpts = SharedOpts & {
  show?: boolean;
  amount: number;
  ease?: EaseOptions;
};

export type CameraControlsConfig = {
  azimuthAngles?: { min: number; max: number };
  polarAngles?: { min: number; max: number };
  distance?: { min: number; max: number };
};
